import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StringNode, widget, WidgetPlugin } from '@sagold/react-json-editor';

import { FormField } from '../../../components/formfield/FormField';
import { ReadOnlyInput } from '../../../components/readonly-input/ReadOnlyInput';
import { SingleSelect } from '../../../components/selects/SingleSelect';
import { SingleSelectWidgetOptions } from '../selectWidgetOptions';
import { resolvePlaceholder, useErrorTransformation } from '../widgetUtils';

export type SelectOptions = SingleSelectWidgetOptions;

export const SelectWidget = widget<StringNode<SelectOptions>, string | null>(
  ({ node, options, setValue }) => {
    const { t } = useTranslation();
    const hasError = node.errors.length > 0;
    const titles = (node.schema.options?.enum as string[]) ?? [];
    const errors = useErrorTransformation(node.errors);
    const enumValues = useMemo(
      () =>
        // Filter null from schema.enum, as it should not appear as option in the single select.
        // It has to be present as allowed value in the schema when the selection is not required
        // to pass the validation in case nothing is selected.
        (node.schema.enum?.filter((v) => v != null) || []) as string[],
      [node.schema.enum]
    );

    // Reset initial value if it was set to a string not contained in the enum list.
    // Skip in readOnly — We shouldn't alter data there and just display what was stored.
    useEffect(() => {
      if (
        !options.readOnly &&
        node.value != null &&
        !enumValues.includes(node.value)
      ) {
        setValue(null);
      }
    }, [enumValues, node.value, options.readOnly, setValue]);

    return (
      <FormField
        widgetType="select"
        node={node}
        errors={options.readOnly ? [] : errors}
        options={options}
        showError={!options.readOnly}
        tableLayout={options.tableLayout}
      >
        {options.readOnly ? (
          <ReadOnlyInput
            description={options.description}
            label={options.title}
            value={node.value}
            placeholder={options.placeholder}
            tableLayout={options.tableLayout}
          />
        ) : (
          <SingleSelect
            data={enumValues.map((value, index) => ({
              label: titles[index] ?? value,
              value,
            }))}
            description={options.description}
            label={options.title}
            placeholder={resolvePlaceholder(
              options.placeholder,
              t('app.forms.placeholders.select')
            )}
            error={options.showErrors !== false && hasError}
            clearable={options.clearable === true}
            required={options.required}
            disabled={options.disabled}
            loading={options.loading}
            readOnly={options.readOnly}
            value={node.value ?? null}
            tableLayout={options.tableLayout}
            onChange={(value) => setValue(value)}
          />
        )}
      </FormField>
    );
  }
);

export const SelectWidgetPlugin: WidgetPlugin = {
  id: 'select-widget',
  use: (node) =>
    (node.schema.type === 'string' ||
      (Array.isArray(node.schema.type) &&
        node.schema.type.includes('string'))) &&
    Array.isArray(node.schema.enum),
  Widget: SelectWidget,
};
